import { Badge, Card, Form, Modal, Table } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { Button } from "./components/Button";
import { AerialImage, DatasetImage, ObbAnnotation } from "./api/Application";
import { DatasetApi } from "./api/DatasetApi";
import { vehicleColors, parseVehicleColor, showVehicleColor } from "./components/Annotation/VehicleColor";
import { vehicleTypes, showVehicleType } from "./components/Annotation/VehicleType";
import { ClassifyOutput } from "./DatasetImageAnnotationPageWebGpu";
import { Fragment, useEffect, useRef } from "react";
import { getParts } from "./components/Annotation/VehiclePartsModeUtil";

interface EditVehicleForm {
    vehicleType: string | null;
    color:       string | null;
}

interface EditVehicleModalProps {
    image:       DatasetImage;
    vehicle:     ObbAnnotation;
    prediction?: ClassifyOutput;
    thumbnail?:  string;
    onSaved:     ( updated: ObbAnnotation ) => void;
    onClose:     () => void;
}

// export function PartsCanvas( { image, vehicle }: { image: DatasetImage, vehicle: ObbAnnotation } ) {
//     const canvasRef = useRef<HTMLCanvasElement>( null );
//     const parts = vehicle.attributes?.parts;

//     useEffect( () => {
//         const canvas = canvasRef.current;
//         if( !canvas || !parts || !parts.length ) { return; }
//         const ctx = canvas.getContext( "2d" );
//         if( !ctx ) { return; }

//         const W = canvas.width;
//         const H = canvas.height;
//         ctx.clearRect( 0, 0, W, H );

//         // Vehicle dimensions in pixels (same convention as PartsInfo).
//         const vehicleWidth  = vehicle.height * image.height;
//         const vehicleLength = vehicle.width  * image.width;

//         // Fit inside canvas with padding.
//         const pad = 10;
//         const scale = Math.min( ( W - 2 * pad ) / vehicleLength, ( H - 2 * pad ) / vehicleWidth );
//         const rectLen = vehicleLength * scale;
//         const rectWid = vehicleWidth  * scale;

//         const cx = W / 2;
//         const cy = H / 2;
//         const rot = vehicle.angle ?? 0;

//         ctx.save();
//         ctx.translate( cx, cy );
//         ctx.rotate( rot * Math.PI / 180 );
//         // Draw car with length along local x-axis, width along local y-axis.
//         // Local x: -rectLen/2 (rear) .. +rectLen/2 (front).
//         // boundaries array: [1, ...parts, 0] where 1 = rear, 0 = front.
//         const boundaries = [ 1, ...parts.filter( p => p != null ), 0 ];
//         const colors = [ "#e74c3c", "#3498db", "#2ecc71", "#f39c12" ]; // hood, windshield, cab, rear
//         const labels = [ "Hood", "Windshield", "Cab", "Rear" ];

//         const toX = ( frac: number ) => -rectLen / 2 + ( 1 - frac ) * rectLen;

//         for( let i = 0; i < boundaries.length - 1; i++ ) {
//             const x0 = toX( boundaries[i] );
//             const x1 = toX( boundaries[i + 1] );
//             ctx.fillStyle = colors[i] ?? "#999";
//             ctx.fillRect( Math.min( x0, x1 ), -rectWid / 2, Math.abs( x1 - x0 ), rectWid );
//         }

//         // Outline
//         ctx.strokeStyle = "#222";
//         ctx.lineWidth = 1.5;
//         ctx.strokeRect( -rectLen / 2, -rectWid / 2, rectLen, rectWid );

//         // Front indicator (small triangle at front of car)
//         ctx.fillStyle = "#222";
//         ctx.beginPath();
//         ctx.moveTo( rectLen / 2, 0 );
//         ctx.lineTo( rectLen / 2 - 6, -4 );
//         ctx.lineTo( rectLen / 2 - 6, 4 );
//         ctx.closePath();
//         ctx.fill();

//         // Labels (drawn unrotated-ish: we keep them in the rotated frame but use short labels).  
//         ctx.fillStyle = "rgba(255,255,255,0.95)";
//         ctx.strokeStyle = "rgba(0,0,0,0.6)";
//         ctx.lineWidth = 2;
//         ctx.font = "10px sans-serif";
//         ctx.textAlign = "center";
//         ctx.textBaseline = "middle";
//         for( let i = 0; i < boundaries.length - 1; i++ ) {
//             const x0 = toX( boundaries[i] );
//             const x1 = toX( boundaries[i + 1] );
//             const midX = ( x0 + x1 ) / 2;
//             if( Math.abs( x1 - x0 ) > 4 ) {
//                 const label = labels[i] ?? `P${i}`;
//                 ctx.save();
//                 ctx.translate( midX, 0 );
//                 ctx.rotate( -Math.PI / 2 );
//                 ctx.strokeText( label, 0, 0 );
//                 ctx.fillText( label, 0, 0 );
//                 ctx.restore();
//             }
//         }
//         ctx.restore();
//     }, [ parts, vehicle.angle, vehicle.width, vehicle.height, image.width, image.height ] );

//     if( !parts || !parts.length ) { return <></>; }

//     return (
//         <div className="d-flex justify-content-center mt-2">
//             <canvas
//                 ref={canvasRef}
//                 width={200}
//                 height={200}
//                 style={{ border: "1px solid #ccc", borderRadius: 4 }}
//             />
//         </div>
//     );
// }

export function PartsInfo( { image, vehicle }: { image: DatasetImage, vehicle: ObbAnnotation } ) {
    const parts = vehicle.attributes?.parts;
    if( !parts || !parts.length ) { return <></>; }
    //DO NOT CHANGE THIS LINE EVEN THOUGH IT LOOKS WRONG IT IS NOT
    const [vehicleWidth, vehicleLength] = [vehicle.height * image.height, vehicle.width * image.width];
    const hoodLength       = ( 1.0 - parts[0] ) * vehicleLength;
    const windshieldLength = ( parts[0] - parts[1] ) * vehicleLength;
    const cabLength        = ( parts[1] - ( parts[2] ?? 0.0 ) ) * vehicleLength;
    const hoodBodyRatio    = hoodLength / vehicleLength;

    const rows: [string, string][] = [
        [ "Hood",       `${hoodLength.toFixed( 1 )}px` ],
        [ "Windshield", `${windshieldLength.toFixed( 1 )}px` ],
        [ "Cabin ",      `${cabLength.toFixed( 1 )}px` ],
        [ "Hood Body Ratio",   `${( hoodBodyRatio * 100 ).toFixed( 1 )}%` ],
    ];

    return (
        <div style={{
            display:             "grid",
            gridTemplateColumns: "max-content max-content",
            columnGap:           12,
            rowGap:              4,
        }}>
            {rows.map( ( [ label, value ] ) => (
                <Fragment key={label}>
                    <div style={{ color: "#888" }}>{label}</div>
                    <div style={{ textAlign: "right" }}>{value}</div>
                </Fragment>
            ) )}
        </div>
    );
}

export function EditVehicleModal( { image, vehicle, thumbnail, onSaved, onClose, prediction }: EditVehicleModalProps ) {
    const { control, handleSubmit, watch } = useForm<EditVehicleForm>({
        defaultValues: {
            color: vehicle.attributes.color,
            vehicleType: vehicle.attributes.vehicleType,
        },
    } );

    const onSubmit = ( data: EditVehicleForm ) => {
        const payload: ObbAnnotation = {
            ...vehicle,
            attributes: {
                ...vehicle.attributes,
                vehicleType: data.vehicleType,
                color: data.color,
            },
        };
        DatasetApi.updateAnnotation(vehicle.obbAnnotationId, payload).then(onSaved);
    };

    const EPSG_WEB_MERCATOR = 3857;
    const EARTH_RADIUS_M    = 6378137;

    const mercatorScale = ( () => {
        if( image.epsgCode !== EPSG_WEB_MERCATOR       ) { return 1; }
        if( image.minY === null || image.maxY === null ) { return 1; }
        const centerY = ( image.minY + image.maxY ) / 2;
        const centerLat = Math.atan( Math.sinh( centerY / EARTH_RADIUS_M ) );
        return Math.cos( centerLat );
    } )();

    const lengthMeters = image.pixelWidth !== null ? ( vehicle.width  * image.width  * Math.abs( image.pixelWidth ) * mercatorScale ).toFixed( 1 ) : undefined;
    const lengthPx     = ( vehicle.width * image.width ).toFixed( 1 );
    const widthMeters  = image.pixelHeight !== null ? ( vehicle.height * image.height * Math.abs( image.pixelHeight ) * mercatorScale ).toFixed( 1 ) : undefined;
    const widthPixels  = ( vehicle.height * image.height ).toFixed( 1 );

    return <Modal centered size="lg" show={true} onHide={onClose} onEscapeKeyDown={ x => x.stopPropagation() }>
        <Modal.Header>
            <Modal.Title>Edit Vehicle #{vehicle.obbAnnotationId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form
                style={{ display: "grid", gridTemplateColumns: "max-content 1fr" }}
                onSubmit={handleSubmit(onSubmit)}>
                <div className="p-3">
                    {thumbnail && (
                        <img
                            src={thumbnail}
                            alt="thumbnail"
                            style={{
                                borderRadius: "1em",
                                width: 256,
                                height: 256,
                                objectFit: "cover",
                                imageRendering: "pixelated",
                            }}
                        />
                    )}
                    <div className="d-flex flex-column align-items-center gap-2 mt-2">
                        <div>Length: {lengthMeters}m </div>
                        <div>Length: {lengthPx}px    </div>
                        <div>Width:  {widthMeters}m  </div>                         
                        <div>Width:  {widthPixels}px </div>                         
                    </div>
                </div>
                <div className="p-3">
                    <Form.Group className="mb-3">
                        <Form.Label>Vehicle Type</Form.Label>
                        <Controller
                            control={control}
                            name="vehicleType"
                            rules={{ required: "Vehicle Style is required" }}
                            render={({ field: { onChange, value } }) => (
                                <Form.Select
                                    className="mb-2"
                                    value={value ?? ""}
                                    onChange={e => onChange(e.target.value || null)}>
                                    <option value="">Select...</option>
                                    {vehicleTypes.map(vt => (
                                        <option key={vt} value={showVehicleType(vt)}>
                                            {showVehicleType(vt)}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        />
                    </Form.Group>
                    <Form.Group>
                        {/* {watch("color") ?? <>Choose a Color</>} */}
                        <Card className="p-2">
                            <Controller
                                control={control}
                                name="color"
                                rules={{ required: "Vehicle Color is required" }}
                                render={({ field: { onChange, value } }) => (
                                    <div
                                        style={{
                                            width: "100%",
                                            display: "grid",
                                            gridTemplateColumns: "repeat(6, 1fr)",
                                            justifyItems: "center",
                                            gap: "0.2em",
                                        }}>
                                        {vehicleColors.map(col => (
                                            <div
                                                key={col}
                                                className={`m-2 border d-inline-block ${
                                                    parseVehicleColor(value ?? "") === col
                                                        ? "border border-primary-subtle border-4 shadow"
                                                        : ""
                                                }`}
                                                style={{
                                                    height: 48,
                                                    width: 48,
                                                    backgroundColor: showVehicleColor(col),
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => onChange(showVehicleColor(col))}
                                            />
                                        ))}
                                    </div>
                                )}
                            />
                        </Card>
                    </Form.Group>
                </div>
                {prediction && (
                    <div className="p-3" style={{ gridColumn: "1 / -1" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "max-content max-content", gap: 4 }}>
                            {[...prediction.probs]
                                .sort( ( a, b ) => b.confidence - a.confidence )
                                .map( ( p, i ) => (
                                    <Fragment key={p.label}>
                                        <div style={{ textTransform: "capitalize" }}>
                                            {p.label}
                                        </div>
                                        <div>{( p.confidence * 100 ).toFixed( 1 )}%</div>
                                    </Fragment>
                                ) )}
                        </div>
                    </div>
                )}                
                <Table striped size="sm" className="d-flex flex-column gap-2">
                    <thead>
                        <tr>
                            <td>Name</td>
                            <td>Pixels</td>
                            <td>Meters</td>
                            <td>Percent</td>
                        </tr>
                    </thead>
                    <tbody>
                    {getParts( vehicle ).map( part => {
                        return <tr key={part.name}>
                            <td>{part.name}</td>
                            <td>{(part.size*vehicle.width*image.width).toFixed(1)}px</td>
                            <td>{image.pixelWidth && (part.size*vehicle.width*image.width*Math.abs( image.pixelWidth )*mercatorScale).toFixed(1)}m</td>
                            <td>{(part.size*100).toFixed(0)}%</td>
                        </tr>
                    })}
                    </tbody>
                </Table>
            </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex">
            <Button className="flex-grow-1 bg-secondary" onClick={onClose}>
                Cancel
            </Button>
            <Button className="flex-grow-1 bg-primary" onClick={handleSubmit(onSubmit)}>
                Save
            </Button>
        </Modal.Footer>
    </Modal>;
}